<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TeletelFlowsAPI from 'dashboard/api/teletel/flows';

const emit = defineEmits(['imported']);
const { t } = useI18n();
const showImport = ref(false);
const payload = ref('');
const preview = ref(null);
const error = ref('');
const busy = ref(false);
const exporting = ref(false);
const uncertain = ref(false);
const MAX_BYTES = 1024 * 1024;

watch(payload, () => {
  preview.value = null;
  error.value = '';
});

const canImport = computed(
  () =>
    preview.value?.valid &&
    preview.value.definitions?.length > 0 &&
    !busy.value &&
    !uncertain.value
);

const closeImport = () => {
  if (busy.value) return;
  showImport.value = false;
  payload.value = '';
  preview.value = null;
  error.value = '';
  uncertain.value = false;
};

const readFile = async event => {
  const [file] = event.target.files || [];
  if (!file || busy.value) return;
  preview.value = null;
  error.value = '';
  if (file.size > MAX_BYTES) {
    error.value = t('TELETEL_FLOWS.TRANSFER.TOO_LARGE');
    return;
  }
  busy.value = true;
  try {
    payload.value = await file.text();
  } catch {
    error.value = t('TELETEL_FLOWS.TRANSFER.FILE_ERROR');
  } finally {
    busy.value = false;
    event.target.value = '';
  }
};

const checkPayload = async () => {
  if (busy.value || uncertain.value) return;
  error.value = '';
  preview.value = null;
  if (new Blob([payload.value]).size > MAX_BYTES) {
    error.value = t('TELETEL_FLOWS.TRANSFER.TOO_LARGE');
    return;
  }
  try {
    const parsed = JSON.parse(payload.value);
    if (
      parsed?.version !== 1 ||
      !Array.isArray(parsed.definitions) ||
      !parsed.definitions.length
    ) {
      throw new Error('invalid definition file');
    }
  } catch {
    error.value = t('TELETEL_FLOWS.TRANSFER.INVALID_FILE');
    return;
  }
  busy.value = true;
  try {
    const { data } = await TeletelFlowsAPI.previewImport(payload.value);
    preview.value = data;
  } catch (failure) {
    if (failure.response?.status === 422) preview.value = failure.response.data;
    else error.value = t('TELETEL_FLOWS.TRANSFER.PREVIEW_ERROR');
  } finally {
    busy.value = false;
  }
};

const importDefinitions = async () => {
  if (!canImport.value) return;
  busy.value = true;
  error.value = '';
  try {
    const { data } = await TeletelFlowsAPI.importDefinitions(payload.value);
    showImport.value = false;
    payload.value = '';
    preview.value = null;
    useAlert(
      t('TELETEL_FLOWS.TRANSFER.IMPORTED', { count: data.payload.length })
    );
    emit('imported');
  } catch (failure) {
    preview.value = null;
    if (failure.response?.status === 422) {
      preview.value = failure.response.data;
    } else {
      uncertain.value = true;
      error.value = t('TELETEL_FLOWS.TRANSFER.IMPORT_UNCERTAIN');
    }
  } finally {
    busy.value = false;
  }
};

const exportDefinitions = async () => {
  if (exporting.value) return;
  exporting.value = true;
  try {
    const { data } = await TeletelFlowsAPI.exportDefinitions();
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `teletel-flows-${Date.now()}.json`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch {
    useAlert(t('TELETEL_FLOWS.TRANSFER.EXPORT_ERROR'));
  } finally {
    exporting.value = false;
  }
};
</script>

<template>
  <section class="flex flex-col gap-3 rounded-lg border border-n-weak p-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('TELETEL_FLOWS.TRANSFER.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('TELETEL_FLOWS.TRANSFER.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex gap-2">
        <NextButton
          slate
          sm
          :disabled="exporting"
          :is-loading="exporting"
          @click="exportDefinitions"
        >
          {{ t('TELETEL_FLOWS.TRANSFER.EXPORT') }}
        </NextButton>
        <NextButton slate sm :disabled="busy" @click="showImport = true">
          {{ t('TELETEL_FLOWS.TRANSFER.IMPORT') }}
        </NextButton>
      </div>
    </div>
    <div v-if="showImport" class="flex flex-col gap-3">
      <label class="text-sm text-n-slate-12">
        {{ t('TELETEL_FLOWS.TRANSFER.FILE') }}
        <input
          type="file"
          accept=".json,application/json"
          :disabled="busy || uncertain"
          @change="readFile"
        />
      </label>
      <label class="text-sm text-n-slate-12">
        {{ t('TELETEL_FLOWS.TRANSFER.PAYLOAD') }}
        <textarea
          v-model="payload"
          rows="8"
          dir="ltr"
          class="w-full font-mono text-sm"
          :disabled="busy || uncertain"
        />
      </label>
      <p class="text-sm text-n-slate-11">
        {{ t('TELETEL_FLOWS.TRANSFER.DRAFTS_ONLY') }}
      </p>
      <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <div
        v-if="preview"
        class="flex flex-col gap-2 text-sm"
        aria-live="polite"
      >
        <p v-if="preview.valid" class="text-n-teal-11">
          {{
            t('TELETEL_FLOWS.TRANSFER.PREVIEW_READY', {
              count: preview.definitions.length,
            })
          }}
        </p>
        <ul v-if="preview.valid" class="list-disc ps-5">
          <li v-for="(definition, index) in preview.definitions" :key="index">
            {{ definition.name }}
          </li>
        </ul>
        <ul v-else class="list-disc ps-5 text-n-ruby-11">
          <li v-for="(item, index) in preview.errors" :key="index">
            {{
              item.index === null
                ? item.error
                : t('TELETEL_FLOWS.TRANSFER.DEFINITION_ERROR', {
                    index: item.index + 1,
                    message: item.error,
                  })
            }}
          </li>
        </ul>
      </div>
      <div class="flex flex-wrap gap-2">
        <NextButton
          slate
          sm
          :disabled="busy || uncertain || !payload.trim()"
          :is-loading="busy"
          @click="checkPayload"
        >
          {{ t('TELETEL_FLOWS.TRANSFER.PREVIEW') }}
        </NextButton>
        <NextButton
          sm
          :disabled="!canImport"
          :label="t('TELETEL_FLOWS.TRANSFER.CONFIRM')"
          @click="importDefinitions"
        />
        <NextButton
          slate
          sm
          :disabled="busy"
          :label="t('TELETEL_FLOWS.TRANSFER.CLOSE')"
          @click="closeImport"
        />
      </div>
    </div>
  </section>
</template>
